import { FC, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Header from 'Components/Layout/Header'
import Table from 'Components/Layout/Table'
import StudioTable from '../../../Table/Admin/Edition/StudioTable'
import DeleteModal from '../../../Components/Modal/DeleteModal'
import { Button } from 'react-bootstrap'
import FormEmptyOffCanvas from '../../../Components/Modal/FormEmptyOffCanvas'
import Creation from 'Entity/Creation'
import CreationModal from 'Components/Studio/CreationModal'
import CreationLocationModal from 'Components/Studio/CreationLocationModal'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'Vendor/Store/store'
import { UserContext } from 'Vendor/Context/UserContextProvider'
import { fetchAssetsThunk } from 'Vendor/Store/platformAsset/thunk'
import DownloadModal from 'Components/Studio/_DownloadModal'
import Api from 'Vendor/Api'

const StudioPage: FC = () => {
    const { t } = useTranslation()

    const [toggleReload, setToggleReload] = useState<boolean>(true)
    const [show, setShow] = useState<boolean>(false)
    const [search, setSearch] = useState<string>('')
    const [creations, setCreations] = useState<Creation[]>([])
    const [creation, setCreation] = useState<Creation>({} as Creation)
    const [showCreationModal, setShowCreationModal] = useState(false)
    const [showCreationLocation, setShowCreationLocation] = useState<boolean>(false)
    const [isDuplicate, setIsDuplicate] = useState(false);
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const saveStatus = useSelector((state: RootState) => state.studio.present.status);
    const prevSaveStatus = useRef(saveStatus);
    const {currentUser} = useContext(UserContext);
    const dispatch: AppDispatch = useDispatch();

    // fetch platform assets
    useEffect(()=> {
        const platforms = currentUser?.currentOrganization?.platforms || [];

        if (platforms.length > 0) {
            dispatch(fetchAssetsThunk(platforms));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser?.currentOrganization?.platforms, dispatch])

    useEffect(() => {
        if (saveStatus === 'succeeded') {
            setToggleReload(prev => !prev);
        }

        prevSaveStatus.current = saveStatus;
    }, [saveStatus]);

    const handleClose = (reload = false) => {
        setShow(false);
        setIsDuplicate(false);

        if (reload) {
            setToggleReload(!toggleReload);
        }
    }

    const handleShow = () => setShow(true)

    const [showDelete, setShowDelete] = useState<boolean>(false)

    const handleDeleteClose = (reload = false) => {
        setCreation({} as Creation)
        setShowDelete(false)
        if (reload) {
            setToggleReload(!toggleReload)
        }
    }
    const handleDeleteShow = () => setShowDelete(true)

    const onSuccess = (creation: Creation) => {
        setCreation(creation)
        setShow(false)
        setToggleReload(!toggleReload)
    }

    const addCreation = () => {
        setCreation({} as Creation)
        handleShow()
    }

    const editCreation = (creation: Creation) => {
        setCreation(creation)
        setShow(true)
    }

    const deleteCreation = (creation: Creation) => {
        setCreation(creation)
        handleDeleteShow()
    }

    const showStudioModal = (creation: Creation) => {
        setCreation(creation)
        setShowCreationModal(true)
    }


    const openCreationLocationModal = (creation: Creation) => {
        setCreation(creation);
        setShowCreationLocation(true);
        requestAnimationFrame(() => {
            document.body.classList.add('showCreationLocationModal');
            const modal = document.getElementById('creationLocationModal');
            if (modal) modal.style.top = `${window.scrollY}px`;
        });
    };

    const closeCreationLocationModal = (refresh: boolean = false) => {
        document.body.classList.remove('showCreationLocationModal');
        if (refresh) setToggleReload(prev => !prev); // safe boolean toggle
        setTimeout(() => setShowCreationLocation(false), 300); // match CSS animation
    };

    const handleCreationDownload = async (type: 'pdf' | 'jpg' | 'png' | 'html' = 'pdf') => {
        try {
            setIsDownloading(true);

            const response = await Api.post(
                `/studio/creation/${creation.id}/export?type=${type}`,
                {},
                { responseType: 'blob' }
            );

            let mimeType = '';
            let extension = '';

            switch (type) {
                case 'pdf':
                    mimeType = 'application/pdf';
                    extension = 'pdf';
                    break;
                case 'jpg':
                    mimeType = 'image/jpeg';
                    extension = 'jpg';
                    break;
                case 'png':
                    mimeType = 'image/png';
                    extension = 'png';
                    break;
                case 'html':
                    mimeType = 'text/html';
                    extension = 'html';
                    break;
            }

            const blob = new Blob([response.data], { type: mimeType });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = `creation_${creation.id}.${extension}`;
            document.body.appendChild(a);
            a.click();
            a.remove();

            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error(`Failed to download ${type.toUpperCase()}:`, err);
        } finally {
            setIsDownloading(false);
            setCreation({} as Creation)
            setShowDownloadModal(false);
        }
    };

    const actionsRender = (creation: Creation) => (
        <>
            <Button
                variant='primary' size='sm'
                onClick={() => showStudioModal(creation)}
            ><i className="fa-solid fa-edit"></i></Button>
            <Button
                variant="primary"
                size="sm"
                onClick={() => openCreationLocationModal(creation)}>
                    <i className="fa-solid fa-location-dot"></i>
            </Button>
            <Button
                variant="primary"
                size="sm"
                onClick={() => {
                    setIsDuplicate(true);
                    setShow(true);
                    setCreation(creation);
                }}
            >
                <i className="fa-solid fa-clone"></i>
            </Button>
            <Button
                variant="primary"
                size="sm"
                onClick={() => {
                    setShowDownloadModal(true);
                    setCreation(creation);
                }}
            >
                <i className="fa-solid fa-download"></i>
            </Button>
            {creation?.isGranted?.edit && (
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => editCreation(creation)}
                >
                    <i className="fa-solid fa-pencil me-2"></i>
                    {t('app.edit')}
                </Button>
            )}
            {creation?.isGranted?.delete && (
                <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => deleteCreation(creation)}
                >
                    <i className="fa-solid fa-trash me-2"></i>
                    {t('app.delete')}
                </Button>
            )}
        </>
    )

    let table = StudioTable
    if (table.hasOwnProperty('actions')) {
        table.actions.render = actionsRender
    }

    return (
        <>
            <Header
                title={t('app.studio_label')}
                add={addCreation}
                search={search}
                setSearch={setSearch}
            />
            <Table
                path="/studio/creation"
                rows={table}
                toggleReload={toggleReload}
                items={creations}
                setItems={setCreations}
                keyword={search}
                setKeyword={setSearch}
            />
            <FormEmptyOffCanvas
                i18nTitle={
                    isDuplicate
                        ? t('app.duplicate_creation', { name: creation.name })
                        : creation.id
                            ? t('app.edit_creation')
                            : t('app.add_creation')
                }
                show={show}
                handleClose={handleClose}
                onSuccess={onSuccess}

                path={
                    isDuplicate
                        ? `/studio/creation/${creation.id}/duplicate`
                        : `/studio/creation/${creation.id ?? 'new'}`
                }

                formPath={isDuplicate ? true : creation.id ? true : false}

                method={isDuplicate ? 'post' : creation.id ? 'patch' : 'post'}
            />
            <DeleteModal
                handleClose={handleDeleteClose}
                show={showDelete}
                path={'/studio/creation/' + (creation?.id ?? 'error')}
                name={
                    '#' + (creation ? `${creation.id} - ${creation.name}` : 'error')
                }
                archive={true}
            />
            <CreationModal
                creation={creation}
                show={showCreationModal}
                setShow={setShowCreationModal}
            />

            <DownloadModal
                show={showDownloadModal}
                onHide={() => setShowDownloadModal(false)}
                onSubmit={({ exporterType }) => handleCreationDownload(exporterType as 'pdf' | 'jpg' | 'png' | 'html')}
                isLoading={isDownloading}
            />

            {showCreationLocation && <CreationLocationModal handleClose={closeCreationLocationModal} creation={creation} /> }
        </>
    )
}

export default StudioPage
